import { Fragment, useMemo, useState, VFC } from "react";
import { useTable, useSortBy, TableOptions, Column, useFilters, useGlobalFilter, usePagination, useExpanded, ColumnInstance } from "react-table";
import { CellRenderer, HeaderRenderer } from ".";
import { FilterRenderers, HeaderRenderers, Renderers, TableSortableColumns } from "./types";
import { _ } from "i18n";
import Loader from "../Loader";
import Icon from "../Icon";
import { Form, Field } from "react-final-form";
import { handlePageCount } from "./functions";
import { Keys } from "i18n/locales/types";
import { compareValues, sortTransformers } from "../../helpers/apiManagement/sorting";

enum SortState {
	UNSORTED = 0,
	ASCENDING = 1,
	DESCENDING = 2
}

interface DatagridProps<T extends object> {
	columns: {
		[K in keyof T]: string;
	};
	data: T[];
	cellRenderers?: Renderers<T>;
	headerRenderers?: HeaderRenderers<T>;
	filterRenderers?: FilterRenderers<T>;
	hiddenColumns?: string[];
	className?: string;
	override: boolean;
	headerClassName?: string;
	onRowClick?: (row: T, index: number) => void;
	preselectedRow?: number;
	defaultRenderers?: { Header: HeaderRenderer<T>; Cell: CellRenderer<T> };
	onPaginate?: (pageSize: number, nextPageIndex: number) => void;
	totalRecords?: number;
	disableSoryBy?: boolean;
	sortableColumns?: string[];
	lengthFilterEnabled?: boolean;
	manualPaginationEnabled?: boolean;
	showLoader?: boolean;
	searchEnabled?: boolean;
	onSearch?: (values: any) => void;
	defaultLength?: number;
	renderRowSubComponent?: (row: any) => JSX.Element;
	onSortBy?: (column: { id: string; desc: boolean } | null) => void;
	manualSortBy?: boolean;
	columnSortTypes?: { [key: string]: 'datetime' | 'alphanumeric' | 'string' | 'number' };
	buttonFilterEnabled?: boolean;
	buttonFilterLabel?: string;
	onButtonFilterClick?: () => void;
}

function makeDefaultRenderers<T extends object>(): { Header: HeaderRenderer<T>; Cell: CellRenderer<T> } {
	return {
		Header: ({ column }, headerName) => {
			return (
				<>
					{headerName}
					<span>
						{column.isSorted ? column.isSortedDesc ? <Icon icon="icon-zoom-arrow-top" /> : <Icon icon="icon-zoom-arrow-bottom" /> : ""}
					</span>
					{/* @ts-ignore */}
					<span className="tooltiptext">{column.tooltip}</span>
				</>
			);
		},
		Cell: ({ cell }) => (cell.value === 0 ? <span className="zero-value">{cell.value}</span> : cell.value),
	};
}

export const Datagrid = <T extends Record<string, any>>({
	columns: columnsDefintion,
	data,
	className,
	override,
	cellRenderers,
	headerRenderers,
	filterRenderers,
	hiddenColumns = [],
	defaultRenderers = makeDefaultRenderers<T>(),
	onPaginate,
	totalRecords,
	disableSoryBy,
	sortableColumns = [],
	lengthFilterEnabled,
	manualPaginationEnabled,
	showLoader,
	searchEnabled,
	onSearch,
	defaultLength,
	renderRowSubComponent,
	onSortBy,
	manualSortBy = false,
	columnSortTypes,
	buttonFilterEnabled,
	buttonFilterLabel,
	onButtonFilterClick
}: DatagridProps<T>): ReturnType<VFC<DatagridProps<T>>> => {
	let columns: TableOptions<T>["columns"];
	const [currentPage, setCurrentPage] = useState<number>(0);
	const [currentPageSize, setCurrentPageSize] = useState<number>(defaultLength ? defaultLength : 10);
	const [sortCycle, setSortCycle] = useState<{[key: string]: number}>({});

	columns = useMemo(
		() =>
			Object.entries(columnsDefintion).map(([accessor, Header]) => {
				return { accessor, Header };
			}),
		[columnsDefintion]
	);

	if (override) {
		columns = useMemo(
			() =>
				Object.entries(columnsDefintion).map(([accessor, headerName]) => {
					return {
						accessor,
						Header: (props) => {
							let renderer = undefined;
							if (headerRenderers) {
								renderer = headerRenderers[accessor];
							}

							return renderer !== undefined ? renderer(props, headerName) : defaultRenderers.Header(props, headerName);
						},
						sortDescFirst: !manualSortBy,
						sortType: columnSortTypes?.[accessor] || 'string',

						Cell: (props: any) => {
							let renderer = undefined;
							if (cellRenderers) {
								renderer = cellRenderers[accessor];
							}

							return renderer !== undefined ? renderer(props) : defaultRenderers.Cell(props);
						},

						tooltip: _(`game.stats.table.${accessor}` as keyof Keys),
						Filter: () => {
							let renderer = undefined;
							if (filterRenderers) {
								renderer = filterRenderers[accessor];
							}
							return renderer;
						},
						disableSortBy: sortableColumns.includes(TableSortableColumns.SORT_ALL_COLUMNS)
							? false
							: sortableColumns.includes(accessor)
							? false
							: true,
					} as Column<T>;
				}),
			[columnsDefintion, headerRenderers, columnSortTypes]
		);
	}

	const tableInstance = useTable(
		{
			columns,
			data,
			initialState: {
				hiddenColumns: hiddenColumns,
				pageIndex: currentPage,
				pageSize: currentPageSize,
			},
			manualPagination: true,
			manualSortBy: manualSortBy,
			pageCount: totalRecords && handlePageCount(totalRecords, currentPageSize),
			disableSortBy: disableSoryBy,
			sortTypes: {
				datetime: (rowA, rowB, columnId) => {
					return compareValues(
						rowA.values[columnId],
						rowB.values[columnId],
						sortTransformers.datetime
					);
				},
				alphanumeric: (rowA, rowB, columnId) => {
					return compareValues(rowA.values[columnId], rowB.values[columnId]);
				},
				string: (rowA, rowB, columnId) => {
					return compareValues(
						rowA.values[columnId],
						rowB.values[columnId],
						sortTransformers.string
					);
				},
				number: (rowA, rowB, columnId) => {
					return compareValues(
						rowA.values[columnId],
						rowB.values[columnId],
						sortTransformers.number
					);
				}
			}
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		useExpanded,
		usePagination
	);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		footerGroups,
		page,
		prepareRow,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		visibleColumns,
		state: { expanded, pageIndex, pageSize, sortBy },
	} = tableInstance;

	const onSearchSubmit = (values: any) => {
		onSearch!(values);
	};

	const getNextSortState = (currentState: SortState): SortState => {
		switch (currentState) {
			case SortState.UNSORTED:
				return SortState.ASCENDING;
			case SortState.ASCENDING:
				return SortState.DESCENDING;
			case SortState.DESCENDING:
				return SortState.UNSORTED;
			default:
				return SortState.ASCENDING;
		}
	};

	const handleSortClick = (columnId: string): void => {
		const currentState = sortCycle[columnId] || SortState.UNSORTED;
		const nextState = getNextSortState(currentState);

		setSortCycle((prev) => ({
			...prev,
			[columnId]: nextState
		}));

		if (nextState === SortState.UNSORTED) {
			tableInstance.setSortBy([]);
			if (manualSortBy && onSortBy) {
				onSortBy(null);
			}
			return;
		}

		const sortConfig = {
			id: columnId,
			desc: nextState === SortState.DESCENDING
		};

		tableInstance.setSortBy([sortConfig]);
		if (manualSortBy && onSortBy) {
			onSortBy(sortConfig);
		}
	};

	const getCustomHeaderProps = (column: ColumnInstance<T>) => {
		const originalProps = column.getHeaderProps(column.getSortByToggleProps());
		return {
			...originalProps,
			onClick: (e: React.MouseEvent) => {
				e.preventDefault();
				if (!column.disableSortBy) {
					handleSortClick(column.id);
				}
			}
		};
	};

	return (
		<>
			{(lengthFilterEnabled || searchEnabled || buttonFilterEnabled) && (
				<div className="datatable-top">
					<div>
						<select
							value={pageSize}
							onChange={(e) => {
								setPageSize(Number(e.target.value));
								setCurrentPageSize(Number(e.target.value));
								onPaginate!(Number(e.target.value), 0);
							}}
						>
							{[10, 20, 30, 40, 50].map((length) => (
								<option key={length} value={length}>
									{_("label.pagination.show")} {length}
								</option>
							))}
						</select>
					</div>
					{searchEnabled && (
						<Form
							onSubmit={onSearchSubmit}
							render={({ handleSubmit, submitting }) => (
								<>
									<form onSubmit={handleSubmit}>
										<div className="form-row">
											<Field name="search" type="text">
												{({ input, meta }) => (
													<>
														<input {...input} placeholder={_("label.search")} className="search" />
														{meta.error && meta.touched && <span className="form-error">{meta.error}</span>}
													</>
												)}
											</Field>
										</div>
									</form>
								</>
							)}
						/>
					)}
					{buttonFilterEnabled && (
						<button className="btn btn-light-blue" onClick={onButtonFilterClick}>
							<Icon icon="icon-filter" />
							{buttonFilterLabel || _("label.filters")}
						</button>
					)}
				</div>
			)}

			<table className={className} {...getTableProps()}>
				<thead>
					{headerGroups.map((headerGroup, rowIndex) => (
						<tr {...headerGroup.getHeaderGroupProps()} key={`row.${rowIndex}`}>
							{headerGroup.headers.map((column, columnIndex) => (
								<th {...getCustomHeaderProps(column)} key={`column-${columnIndex}`}>
									{column.render("Header")}
									{column.canFilter ? column.render("Filter") : null}
								</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{showLoader ? (
						<tr>
							<td colSpan={visibleColumns.length}>
								<Loader />
							</td>
						</tr>
					) : (
						page.map((row) => {
							prepareRow(row);
							return (
								<Fragment key={row.id}>
									<tr {...row.getRowProps()}>
										{row.cells.map((cell, cellIndex) => {
											return <td {...cell.getCellProps()} key={`cell-${cellIndex}`}>{cell.render("Cell")}</td>;
										})}
									</tr>
									{row.isExpanded ? (
										<tr>
											<td></td>
											<td colSpan={visibleColumns.length - 1}>{renderRowSubComponent && renderRowSubComponent({ row })}</td>
										</tr>
									) : null}
								</Fragment>
							);
						})
					)}
				</tbody>
				<tfoot>
					<tr>
						<td colSpan={visibleColumns.length}></td>
					</tr>
				</tfoot>
			</table>

			{manualPaginationEnabled && (totalRecords ? totalRecords > pageSize : false) && (
				<div className="datagrid-pagination">
					<div className="pagination-left">
						<button
							onClick={() => {
								gotoPage(0);
								setCurrentPage(0);
								onPaginate!(pageSize, 0);
							}}
							disabled={!canPreviousPage}
						>
							{
								<>
									<Icon icon="icon-arrow-left" />
									<Icon icon="icon-arrow-left" />
								</>
							}
						</button>
						<button
							onClick={() => {
								previousPage();
								const navPageIndex = pageIndex - 1;
								setCurrentPage(navPageIndex);
								onPaginate!(pageSize, navPageIndex);
							}}
							disabled={!canPreviousPage}
						>
							{
								<>
									<Icon icon="icon-arrow-left" />
								</>
							}
						</button>
						<button
							onClick={() => {
								nextPage();
								const navPageIndex = pageIndex + 1;
								setCurrentPage(navPageIndex);
								onPaginate!(pageSize, navPageIndex);
							}}
							disabled={!canNextPage}
						>
							{
								<>
									<Icon icon="icon-arrow-right" />
								</>
							}
						</button>
						<button
							onClick={() => {
								gotoPage(pageCount - 1);
								const navPageIndex = pageCount - 1;
								setCurrentPage(navPageIndex);
								onPaginate!(pageSize, navPageIndex);
							}}
							disabled={!canNextPage}
						>
							{
								<>
									<Icon icon="icon-arrow-right" />
									<Icon icon="icon-arrow-right" />
								</>
							}
						</button>
					</div>

					<span>
						{_("label.pagination.page")} {pageIndex + 1} {_("label.pagination.of")} {pageOptions.length} |{" "}
						{_("label.pagination.go.to.page")}
					</span>
					<input
						type="number"
						defaultValue={pageIndex + 1}
						onKeyDown={(e) => {
							if (["e", "E", "+", "-", "."].includes(e.key)) {
								e.preventDefault();
							}
						}}
						onChange={(e) => {
							const page = e.target.value ? Number(e.target.value) - 1 : 0;
							gotoPage(page);
							onPaginate!(pageSize, page);
						}}
						style={{ width: "100px" }}
					/>
				</div>
			)}
		</>
	);
};

export default Datagrid;
